import React, { useState, useEffect } from "react";
import {
  Table,
  InputNumber,
  Button,
  Form,
  Row,
  Col,
  Typography,
  Card,
  Tag,
  Empty,
  notification,
} from "antd";
import {
  ShoppingCartOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

// Zambia theme colors
const ZAMBIA_THEME = {
  primary: "#078930", // Zambian green
  secondary: "#EF3340", // Zambian red
  accent: "#FFB81C", // Gold accent
  neutral: "#2D2D2D", // Dark neutral
};

const CommoditySelector = ({ onChange, value = [], disabled = false }) => {
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCommodities, setSelectedCommodities] = useState(value);
  const token = localStorage.getItem("token");

  // Fetch available commodities from API
  useEffect(() => {
    const fetchCommodities = async () => {
      try {
        const response = await axios.get(
          "https://zedlenders.pickmesms.com/items/commodities?filter[status]=published",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCommodities(response.data.data);
      } catch (error) {
        console.error("Error fetching commodities:", error);
        notification.error({
          message: "Error",
          description: "Failed to fetch commodities. Please try again later.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCommodities();
  }, [token]);

  // Update form value when selectedCommodities changes
  // We're not watching onChange to prevent infinite loop
  useEffect(() => {
    if (onChange && !disabled) {
      onChange(selectedCommodities);
    }
  }, [selectedCommodities]);

  // When commodities list updates (e.g., prices edited elsewhere),
  // sync selected items with the latest cost_price to avoid stale pricing.
  useEffect(() => {
    if (!commodities?.length || !selectedCommodities?.length) return;
    const reconciled = selectedCommodities.map((item) => {
      const latest = commodities.find((c) => c.id === item.id);
      if (!latest) return item;
      const latestPrice = Number(latest?.cost_price ?? 0);
      if (latestPrice !== Number(item.selling_price)) {
        const qty = Number(item.quantity || 0);
        return {
          ...item,
          selling_price: latestPrice,
          totalCost: latestPrice * qty,
        };
      }
      return item;
    });
    if (JSON.stringify(reconciled) !== JSON.stringify(selectedCommodities)) {
      setSelectedCommodities(reconciled);
    }
  }, [commodities]);

  // Handle adding a commodity to the selection
  const handleAddCommodity = (commodity, quantity = 1) => {
    // Use cost_price as source of truth
    const unitPrice = Number(commodity?.cost_price ?? 0);
    // Check if we already have this commodity
    const existingIndex = selectedCommodities.findIndex(
      (item) => item.id === commodity.id
    );

    if (existingIndex >= 0) {
      // Update quantity of existing commodity
      const updatedCommodities = [...selectedCommodities];
      updatedCommodities[existingIndex] = {
        ...updatedCommodities[existingIndex],
        quantity: updatedCommodities[existingIndex].quantity + quantity,
        totalCost:
          (updatedCommodities[existingIndex].quantity + quantity) * unitPrice,
      };
      setSelectedCommodities(updatedCommodities);
    } else {
      // Add new commodity to the list
      setSelectedCommodities([
        ...selectedCommodities,
        {
          id: commodity.id,
          name: commodity.commodity,
          cost_price: commodity.cost_price,
          selling_price: unitPrice, // keep selling_price equal to cost_price
          quantity: quantity,
          totalCost: quantity * unitPrice,
          commodity_id: commodity.id, // This will be used when saving to API
        },
      ]);
    }
  };

  // Handle removing a commodity from the selection
  const handleRemoveCommodity = (commodityId) => {
    setSelectedCommodities(
      selectedCommodities.filter((item) => item.id !== commodityId)
    );
  };

  // Handle changing quantity of a selected commodity
  const handleQuantityChange = (value, commodityId) => {
    const commodity = commodities.find((c) => c.id === commodityId);
    if (!commodity) return;

    const quantity = parseInt(value) || 0;

    // Ensure quantity doesn't exceed available stock
    const limitedQuantity = Math.min(quantity, commodity.quantity);
    const unitPrice = Number(commodity?.cost_price ?? 0);

    // Update the selected commodity
    const updatedCommodities = selectedCommodities.map((item) => {
      if (item.id === commodityId) {
        return {
          ...item,
          quantity: limitedQuantity,
          selling_price: unitPrice,
          totalCost: limitedQuantity * unitPrice,
        };
      }
      return item;
    });

    setSelectedCommodities(updatedCommodities);
  };

  // Calculate total cost of all selected commodities
  const totalCost = selectedCommodities.reduce(
    (sum, item) => sum + Number(item.totalCost || 0),
    0
  );

  // Columns for available commodities table
  const availableColumns = [
    {
      title: "Commodity",
      dataIndex: "commodity",
      key: "commodity",
    },
    {
      title: "Selling Price (ZMW)",
      dataIndex: "cost_price",
      key: "selling_price",
      render: (_, record) => {
        const price = Number(record?.cost_price ?? 0);
        return `${price.toLocaleString("en-ZA")} ZMW`;
      },
    },
    {
      title: "Available",
      dataIndex: "quantity",
      key: "quantity",
      render: (qty) => (
        <Tag color={qty > 10 ? ZAMBIA_THEME.primary : ZAMBIA_THEME.secondary}>
          {qty} units
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => handleAddCommodity(record)}
          disabled={disabled || record.quantity <= 0}
          style={{
            background: ZAMBIA_THEME.primary,
            borderColor: ZAMBIA_THEME.primary,
          }}
        >
          Add
        </Button>
      ),
    },
  ];

  // Columns for selected commodities table
  const selectedColumns = [
    {
      title: "Commodity",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Selling Price Per Unit (ZMW)",
      dataIndex: "selling_price",
      key: "selling_price",
      render: (price, record) => {
        const p = Number(record?.cost_price ?? price ?? 0);
        return `${p.toLocaleString("en-ZA")} ZMW`;
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (qty, record) => (
        <InputNumber
          min={1}
          max={commodities.find((c) => c.id === record.id)?.quantity || 1}
          value={qty}
          onChange={(value) => handleQuantityChange(value, record.id)}
          disabled={disabled}
        />
      ),
    },
    {
      title: "Total Cost (ZMW)",
      dataIndex: "totalCost",
      key: "totalCost",
      render: (price, record) => {
        const p = Number(price ?? (record?.quantity || 0) * (record?.selling_price || 0));
        return `${p.toLocaleString("en-ZA")} ZMW`;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveCommodity(record.id)}
          disabled={disabled}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div className="commodity-selector">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <ShoppingOutlined
                  style={{ color: ZAMBIA_THEME.primary, marginRight: 8 }}
                />
                <span>Available Commodities</span>
              </div>
            }
            bordered={false}
          >
            <Table
              dataSource={commodities}
              columns={availableColumns}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <ShoppingCartOutlined
                  style={{ color: ZAMBIA_THEME.primary, marginRight: 8 }}
                />
                <span>Selected Commodities</span>
              </div>
            }
            bordered={false}
          >
            {selectedCommodities.length > 0 ? (
              <>
                <Table
                  dataSource={selectedCommodities}
                  columns={selectedColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </>
            ) : (
              <Empty
                description="No commodities selected"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CommoditySelector;
